/**
 * Module with utilities for interacting with Facebook's "Select Audience" modal
 *
 * This modal is used in multiple places, and has the following properties:
 * - the modal's parent element has the following attrs:
 *  - @aria-label "Select audience"
 *  - @role "dialog"
 * - the modal has two buttons, "DONE" and "CANCEL"
 *
 * The modal arranges options in rows, each of which have the following attrs:
 * - (a) @role "radio" or @role "none", or (b) @type "radio"
 *  - for (a) and (b), the text options are arranged differently
 * - @aria-checked {"true", "false"}
 *
 * These rows are clickable. They have one or descendant divs with text; these
 * provide labels indicating what level of privacy they are associated with.
 *
 * On some pages, Facebook will move "closed" elements into hidden subtrees;
 * this can lead to us clicking the wrong elements. We avoid this by adding
 * checks that `count(ancestor::*[@hidden]) = 0` to some queries.
 */
import { setContext } from '@sentry/react'

import { remoteLog } from '$extensionSrc/utils/remoteLog'

import { FB_PRIVACY_LEVEL } from '../../utils/enums'
import { getDomPromise, throwIfTimeout, wait } from '../interruptiblePromises'
import {
  evaluateXpath,
  getCaseInsensitiveAttribute,
  getCaseInsensitiveString,
  getCaseInsensitiveXPathTextSelector,
} from '../interruptibleXpath'
import { getElement, getOptionalElement } from '../utils'

export class FacebookSelectAudienceModalUtilsException extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'FacebookSelectAudienceModalUtilsException'
  }
}

// Define constants for different privacy levels and parsing utils

export interface PrivacySetting {
  name: string
  level: string
}

export const ONLY_ME = { name: 'Only me', level: FB_PRIVACY_LEVEL.ONLY_ME }
export const FRIENDS = { name: 'Friends', level: FB_PRIVACY_LEVEL.FRIENDS }
export const FRIENDS_OF_FRIENDS = {
  name: 'Friends of friends',
  level: FB_PRIVACY_LEVEL.FRIENDS_OF_FRIENDS,
}

export function getPrivacySettingFromLabel(label: string): string {
  switch (label) {
    case 'Public':
      return FB_PRIVACY_LEVEL.PUBLIC
    case 'Everyone':
      return FB_PRIVACY_LEVEL.PUBLIC
    case 'Possible connections':
      return FB_PRIVACY_LEVEL.PUBLIC
    case 'Friends':
      return FB_PRIVACY_LEVEL.FRIENDS
    case 'Album Contributors Only':
      return FB_PRIVACY_LEVEL.FRIENDS
    case 'Friends (+)':
      return FB_PRIVACY_LEVEL.FRIENDS_OF_FRIENDS
    case 'Your friends of friends':
      return FB_PRIVACY_LEVEL.FRIENDS_OF_FRIENDS
    case 'Friends of friends':
      return FB_PRIVACY_LEVEL.FRIENDS_OF_FRIENDS
    case 'Friends of Album Contributors':
      return FB_PRIVACY_LEVEL.FRIENDS_OF_FRIENDS
    case 'Friends except...':
      return FB_PRIVACY_LEVEL.CUSTOM
    case 'Friends except... (+)':
      return FB_PRIVACY_LEVEL.CUSTOM
    case 'Friends except acquaintances':
      return FB_PRIVACY_LEVEL.CUSTOM
    case 'Specific friends':
      return FB_PRIVACY_LEVEL.CUSTOM
    case 'Only me':
      return FB_PRIVACY_LEVEL.ONLY_ME
    case 'No one':
      return FB_PRIVACY_LEVEL.ONLY_ME
    case 'Custom':
      return FB_PRIVACY_LEVEL.CUSTOM
    default:
      // Default to custom
      remoteLog.info(`getPrivacySettingFromLabel: unrecognized label ${label}`)
      return FB_PRIVACY_LEVEL.CUSTOM
  }
}

export function getLabelsFromOption(option: HTMLElement): string[] {
  const optionLabels = evaluateXpath<HTMLElement>(
    option,
    'descendant::*[text()]',
  ).map((e) => e.textContent)

  if (!optionLabels || optionLabels.length === 0) {
    setContext('Automation Debug', {
      foundOption: option.innerHTML.slice(0, 1000),
    })
    throw new Error(
      `getPrivacySettingFromOption: Could not find any text in option`,
    )
  }

  return optionLabels
}

export function getPrivacySettingFromOption(
  option: HTMLElement,
): PrivacySetting {
  const optionLabels = getLabelsFromOption(option)

  const name = optionLabels[0]
  let level = getPrivacySettingFromLabel(name)
  if (optionLabels?.[1] === 'Your custom list') {
    level = FB_PRIVACY_LEVEL.CUSTOM
  }

  return { name, level }
}

export function getLabelFromSurroundingText(option: HTMLElement): string {
  const candidates = evaluateXpath<HTMLElement>(
    option,
    'preceding::span[text()]',
    { requireDocumentOrder: true },
  ).map((e) => e.textContent)

  if (!candidates || candidates.length === 0) {
    setContext('Automation Debug', {
      foundOption: option.innerHTML.slice(0, 1000),
    })
    throw new Error(
      `getPrivacySettingFromOption: Could not find any text in option`,
    )
  }

  return candidates[candidates.length - 1]
}

export function getPrivacySettingFromSurroundingText(
  option: HTMLElement,
): PrivacySetting {
  const name = getLabelFromSurroundingText(option)
  const level = getPrivacySettingFromLabel(name)

  return { name, level }
}

export function isPrivacySettingAsStrictOrStricter(
  a: PrivacySetting | undefined,
  b: PrivacySetting | undefined,
): boolean {
  // a or b will be undefined if either of them have been deleted from
  // initialState during an automation
  if (a === undefined || b === undefined) {
    return false
  }

  const levelToStrictness = {
    [FB_PRIVACY_LEVEL.ONLY_ME]: 0,
    [FB_PRIVACY_LEVEL.FRIENDS]: 1,
    [FB_PRIVACY_LEVEL.CUSTOM]: 1,
    [FB_PRIVACY_LEVEL.FRIENDS_OF_FRIENDS]: 2,
    [FB_PRIVACY_LEVEL.PUBLIC]: 3,
  }

  return levelToStrictness[a.level] <= levelToStrictness[b.level]
}

export function isPrivacySettingUndoable(setting: PrivacySetting): boolean {
  const cantUndo =
    setting.level === FB_PRIVACY_LEVEL.CUSTOM &&
    (setting.name === 'Friends except...' ||
      setting.name === 'Friends except... (+)' ||
      setting.name === 'Specific friends' ||
      setting.name === 'Custom')

  return !cantUndo
}

// Helpers for interacting with the component

function getSelectAudienceDialog(
  timeoutMs: number = null,
): Promise<HTMLElement> {
  // This might prove insufficient
  // what if the text changes? what if it's not a dialog anymore?
  return getElement<HTMLElement>(
    document,
    `//*[${getCaseInsensitiveXPathTextSelector(
      'aria-label',
      'select audience',
    )} and @role = "dialog" and count(./ancestor::*[@hidden])=0]`,
    { timeout: timeoutMs },
  )
}

function getOptionalSelectAudienceDialog(
  timeoutMs: number = null,
): Promise<HTMLElement> {
  // This might prove insufficient
  // what if the text changes? what if it's not a dialog anymore?
  return getOptionalElement<HTMLElement>(
    document,
    `//*[${getCaseInsensitiveXPathTextSelector(
      'aria-label',
      'select audience',
    )} and @role = "dialog" and count(./ancestor[@hidden]) = 0]`,
    { timeout: timeoutMs },
  )
}

/**
 * Same method as getSelectAudienceDialog, but use evaluateXpath so we can
 * also check for the modal having disappeared (used to wait for the modal
 * to close).
 */
function getSelectAudienceDialogSync(): HTMLElement | undefined {
  return evaluateXpath<HTMLElement>(
    document,
    `//*[${getCaseInsensitiveXPathTextSelector(
      'aria-label',
      'select audience',
    )} and @role = "dialog" and count(./ancestor[@hidden]) = 0]`,
  )[0]
}

async function getActionButton(actionName: string): Promise<HTMLElement> {
  const dialog = await getSelectAudienceDialog(100) // short timeout; dialog should already be there

  let button
  switch (actionName) {
    case 'CANCEL':
      button = evaluateXpath<HTMLElement>(
        dialog,
        `descendant::*[
          contains(
            ${getCaseInsensitiveAttribute('aria-label')},
            'cancel'
          )
          and @role="button"]`,
      )[0]
      break
    case 'DONE':
      button = evaluateXpath<HTMLElement>(
        dialog,
        `descendant::*[(${getCaseInsensitiveXPathTextSelector(
          'aria-label',
          'done',
        )} or ${getCaseInsensitiveXPathTextSelector(
          'aria-label',
          'save',
        )}) and @role="button"]`,
      )[0]
      break
    default:
      throw new Error(
        `getActionButton: Only valid values for actionName {CANCEL, DONE} (received ${actionName})`,
      )
  }

  return button
}

export async function clickAndWaitForModalToOpen(
  clickableElement: HTMLElement,
): Promise<HTMLElement> {
  if (!clickableElement) {
    throw new Error(
      'clickAndWaitForModalToOpen: Could not find clickable element to open modal',
    )
  }
  clickableElement.click()

  const dialog = await getOptionalSelectAudienceDialog(2000)

  if (!dialog) {
    // retry click
    clickableElement.click()

    const selectAudienceText = await getOptionalElement<HTMLElement>(
      document,
      `//*[${getCaseInsensitiveString('text()')} = "select audience"]`,
      { timeout: 500 },
    )

    const dialogElement = await getOptionalElement<HTMLElement>(
      document,
      '//*[@role="dialog"]',
      { timeout: 500 },
    )

    remoteLog.debug(
      'clickAndWaitForModalToOpen: did not find dialog on first click. ' +
        `Select audience element: ${selectAudienceText}, dialog element: ${dialogElement}`,
    )
  }

  return getSelectAudienceDialog(1000)
}

export async function closeModal(
  actionName: string,
  options: {
    timeoutMs?: number
    removeModalManually: boolean
  } = {
    removeModalManually: false,
  },
): Promise<void> {
  let timeoutMs = options.timeoutMs
  if (timeoutMs === null || timeoutMs === undefined) {
    timeoutMs = 1000
  }

  const button = await getActionButton(actionName)

  if (!button) {
    throw new Error(`Could not find element for ${actionName} button`)
  }

  const modalHasClosedListener = getDomPromise(
    document,
    () => !getSelectAudienceDialogSync(),
  )

  button.click()

  if (options.removeModalManually) {
    wait(Math.min(timeoutMs / 2, 100)).then(() => {
      const modal = getSelectAudienceDialogSync()
      if (modal) {
        modal.remove()
      }
    })
  }

  await Promise.race([
    modalHasClosedListener,
    throwIfTimeout(`Modal did not close (waited ${timeoutMs})`, timeoutMs),
  ])
}

export async function clickRowUntilSelected(
  targetRow: HTMLElement,
  originalValue: string | null,
): Promise<void> {
  for (let i = 0; i <= 5; i += 1) {
    targetRow.click()
    const delay = 2 ** i * 100
    await wait(delay)
    const value = targetRow.getAttribute('aria-checked')
    if (value !== originalValue) {
      return
    }
  }
  throw new Error(
    'clickRowUntilSelected: Failed to select target row by clicking',
  )
}

export async function clickRowMatchingPrivacySettingInDialog(
  dialog: HTMLElement,
  privacySetting: PrivacySetting,
): Promise<void> {
  const rows = evaluateXpath<HTMLElement>(
    dialog,
    'descendant::*[(@role="radio" or @role="none" or @type="radio") and @aria-checked]',
  )

  if (rows.length === 0) {
    setContext('Automation Debug', {
      foundDialog: dialog.innerText.slice(0, 1000),
    })
    throw new Error(`clickRowMatchingPrivacySetting: No rows found in dialog`)
  }

  let targetRow
  let fallbackRow
  for (let i = 0; i < rows.length; i += 1) {
    const curRow = rows[i]
    const curSetting =
      curRow.role === 'radio' || curRow.role === 'none'
        ? getPrivacySettingFromOption(curRow)
        : getPrivacySettingFromSurroundingText(curRow)

    if (
      curSetting &&
      curSetting.name === privacySetting.name &&
      curSetting.level === privacySetting.level
    ) {
      targetRow = curRow
      break
    }

    if (
      curSetting &&
      curSetting.level === privacySetting.level &&
      curSetting.level === FB_PRIVACY_LEVEL.FRIENDS &&
      privacySetting.name === 'Friends (+)'
    ) {
      targetRow = curRow
      break
    }

    if (
      curSetting &&
      curSetting.level === privacySetting.level &&
      curSetting.level === FB_PRIVACY_LEVEL.CUSTOM &&
      privacySetting.name === 'Friends except... (+)' &&
      curSetting.name === 'Friends except...'
    ) {
      targetRow = curRow
      break
    }

    // Set a fallback row where level matches even if name does not
    // But continue to look for name and level match
    if (
      curSetting &&
      (curSetting.level === privacySetting.level ||
        curSetting.name === privacySetting.name)
    ) {
      fallbackRow = curRow
    }
  }

  if (!targetRow) {
    if (!fallbackRow) {
      throw new Error(
        `Could not find row matching privacy setting ` +
          `(${privacySetting.name}, ${privacySetting.level})`,
      )
    }
    targetRow = fallbackRow
  }

  const originalValue = targetRow.getAttribute('aria-checked')
  return clickRowUntilSelected(targetRow, originalValue)
}

export async function clickRowMatchingPrivacySetting(
  privacySetting: PrivacySetting,
): Promise<void> {
  const dialog = await getSelectAudienceDialog(100) // should already be open
  return clickRowMatchingPrivacySettingInDialog(dialog, privacySetting)
}

export async function getSelectedPrivacyLevel(): Promise<PrivacySetting> {
  const dialog = await getSelectAudienceDialog(100) // should already be open

  const selectedElement = await getElement<HTMLElement>(
    dialog,
    'descendant::*[@type="radio" and @aria-checked="true"]/ancestor::label',
  )

  const privacyLevel = getPrivacySettingFromOption(selectedElement)
  return privacyLevel
}

// Helper for the icons you click to open the modal

export function getEditPrivacyIcons(
  node: Node,
  options: { includeDisabled?: boolean } | null = null,
): HTMLElement[] {
  if (options && options?.includeDisabled) {
    return evaluateXpath<HTMLElement>(
      node,
      `descendant::*[contains(${getCaseInsensitiveAttribute('aria-label')}, "edit audience") and count(./ancestor::*[@hidden]) = 0]/ancestor-or-self::*[@role="button"]`,
    )
  }
  return evaluateXpath<HTMLElement>(
    node,
    `descendant::*[contains(${getCaseInsensitiveAttribute('aria-label')}, "edit audience") and count(./ancestor::*[@hidden]) = 0]/ancestor-or-self::*[@role="button" and not(@aria-disabled)]`,
  )
}

export async function clickTargetPrivacySettingOptionAndSave(
  isDry: boolean,
  targetValue: PrivacySetting,
): Promise<void> {
  const curValue = await getSelectedPrivacyLevel()

  if (
    !curValue ||
    (curValue.name === targetValue.name && curValue.level === targetValue.level)
  ) {
    return closeModal('CANCEL')
  }

  if (!isPrivacySettingUndoable(targetValue)) {
    return closeModal('CANCEL')
  }

  await clickRowMatchingPrivacySetting(targetValue)
  return closeModal(isDry ? 'CANCEL' : 'DONE')
}
